import { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CircleIcon from '@mui/icons-material/Circle';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { getAgendamientos } from '@/shared/actions/erp';
import type { Agendamiento } from '@/shared/interfaces';

import CalendarioTecnico from '../components/CalendarioTecnico';
import ModalDetalleVisitaAgenda from '../components/ModalDetalleVisitaAgenda';
import {
  estadoVisitaAgenda,
  ESTADO_AGENDA_COLOR,
  ESTADO_AGENDA_LABEL,
  LEYENDA_ESTADOS,
  type EstadoAgendaTag,
} from '../estadoVisitaAgenda';

const TURNOS = [
  { value: 'todos', label: 'Todas las horas' },
  { value: 'manana', label: 'Mañana (00:00–11:59)' },
  { value: 'tarde', label: 'Tarde (12:00–17:59)' },
  { value: 'noche', label: 'Noche (18:00–23:59)' },
];

const clienteDe = (v: any) =>
  v?.prospecto_data?.razon_social ||
  v?.contrato_data?.cliente_data?.razon_social ||
  v?.numero_referencia ||
  'Cliente';

const AgendaPage = () => {
  // Visita seleccionada para el modal de detalle (Calidad de Visita + Ticket).
  const [selected, setSelected] = useState<Agendamiento | null>(null);
  const [openDetail, setOpenDetail] = useState(false);

  // Mes visible del calendario (también define el rango de datos a traer).
  const [cursor, setCursor] = useState<Dayjs>(dayjs());

  // Filtros
  const [fTecnicos, setFTecnicos] = useState<string[]>([]);
  const [fEstado, setFEstado] = useState<'todos' | EstadoAgendaTag>('todos');
  const [fTurno, setFTurno] = useState('todos');

  // Rango a traer: mes visible ± 1 semana (para ver visitas de días colindantes).
  const rango = useMemo(
    () => ({
      desde: cursor.startOf('month').subtract(7, 'day').format('YYYY-MM-DD'),
      hasta: cursor.endOf('month').add(7, 'day').format('YYYY-MM-DD'),
    }),
    [cursor],
  );

  // Fuente = VISITAS del ERP (agendamientos tipo VISITA). Poll cada 30s → refleja
  // cambios de estado sin recargar. La superposición de carga usa solo isLoading
  // (no isFetching) para NO bloquear los clics durante los refetch en segundo plano.
  const { data: agResp, isLoading } = useQuery({
    queryKey: ['agenda-visitas-erp', rango.desde, rango.hasta],
    queryFn: () =>
      getAgendamientos({
        tipo_orden_servicio: 'VISITA',
        fecha_visita_after: rango.desde,
        fecha_visita_before: rango.hasta,
        page: 1,
        page_size: 1000,
        filterByState: false,
      } as any),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const visitas: any[] = useMemo(() => {
    const rows = (agResp as any)?.data?.data;
    return (Array.isArray(rows) ? rows : []).filter((v: any) => v?.fecha_visita);
  }, [agResp]);

  // Técnicos únicos (para el filtro).
  const tecnicos = useMemo(() => {
    const s = new Set<string>();
    visitas.forEach((v) => {
      const n = v.flota_data?.nombre;
      if (n) s.add(n);
    });
    return Array.from(s).sort();
  }, [visitas]);

  const filteredVisitas = useMemo(() => {
    return visitas.filter((v) => {
      if (fTecnicos.length > 0 && !fTecnicos.includes(v.flota_data?.nombre || '')) return false;
      if (fEstado !== 'todos' && estadoVisitaAgenda(v) !== fEstado) return false;
      if (fTurno !== 'todos') {
        const h = parseInt(String(v.hora_visita || '').slice(0, 2), 10);
        if (Number.isNaN(h)) return false;
        if (fTurno === 'manana' && !(h < 12)) return false;
        if (fTurno === 'tarde' && !(h >= 12 && h < 18)) return false;
        if (fTurno === 'noche' && !(h >= 18)) return false;
      }
      return true;
    });
  }, [visitas, fTecnicos, fEstado, fTurno]);

  const onSelect = (v: any) => {
    setSelected(v);
    setOpenDetail(true);
  };
  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelected(null);
  };
  const limpiarFiltros = () => {
    setFTecnicos([]);
    setFEstado('todos');
    setFTurno('todos');
  };
  const hayFiltros = fTecnicos.length > 0 || fEstado !== 'todos' || fTurno !== 'todos';

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventNoteIcon color="primary" />
            AGENDA DE TÉCNICOS
          </Typography>
          <Chip label={`${filteredVisitas.length} de ${visitas.length} visitas`} size="small" color="primary" />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Filtros: técnico, estado, hora */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
          <Autocomplete
            multiple
            size="small"
            options={tecnicos}
            value={fTecnicos}
            onChange={(_, val) => setFTecnicos(val)}
            limitTags={2}
            disableCloseOnSelect
            noOptionsText="Sin técnicos"
            sx={{ minWidth: 280, maxWidth: 480 }}
            renderInput={(params) => (
              <TextField {...params} label="Técnico(s)" placeholder={fTecnicos.length ? '' : 'Buscar técnico…'} />
            )}
          />
          <TextField select size="small" label="Estado" value={fEstado} onChange={(e) => setFEstado(e.target.value as 'todos' | EstadoAgendaTag)} sx={{ minWidth: 180 }}>
            <MenuItem value="todos">Todos los estados</MenuItem>
            {LEYENDA_ESTADOS.map((it) => (
              <MenuItem key={it.tag} value={it.tag}>{it.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Hora" value={fTurno} onChange={(e) => setFTurno(e.target.value)} sx={{ minWidth: 210 }}>
            {TURNOS.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          {hayFiltros && (
            <Button size="small" variant="text" startIcon={<FilterAltOffIcon />} onClick={limpiarFiltros}>
              Limpiar filtros
            </Button>
          )}
        </Box>

        {/* Leyenda de estados */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          {LEYENDA_ESTADOS.map((item) => (
            <Chip
              key={item.tag}
              icon={<CircleIcon sx={{ fontSize: 12, color: `${item.color} !important` }} />}
              label={item.label}
              size="small"
              variant="outlined"
              sx={{ borderColor: item.color, fontSize: '0.75rem' }}
            />
          ))}
        </Box>

        {/* Calendario mensual propio — cada visita es clicable y abre el modal */}
        <CalendarioTecnico
          cursor={cursor}
          setCursor={setCursor}
          visitas={filteredVisitas}
          onSelect={onSelect}
          isLoading={isLoading}
        />

        {/* Lista de visitas en tabla (respeta filtros): ordenada por fecha/hora,
            filas clicables → abren el modal de detalle. */}
        {filteredVisitas.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#334155' }}>
              Lista de visitas ({filteredVisitas.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440, borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f8fafc', color: '#334155' } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Técnico</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...filteredVisitas]
                    .sort((a: any, b: any) =>
                      `${a.fecha_visita || ''}${a.hora_visita || ''}`.localeCompare(
                        `${b.fecha_visita || ''}${b.hora_visita || ''}`,
                      ),
                    )
                    .map((v: any) => {
                      const tag = estadoVisitaAgenda(v);
                      const tecnico = v.flota_data?.nombre || 'Sin técnico';
                      return (
                        <TableRow
                          hover
                          key={v.id_agendamiento}
                          onClick={() => onSelect(v)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{v.fecha_visita ? dayjs(v.fecha_visita).format('DD/MM/YYYY') : '—'}</TableCell>
                          <TableCell>{(v.hora_visita || '').slice(0, 5) || '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{tecnico}</TableCell>
                          <TableCell>{clienteDe(v)}</TableCell>
                          <TableCell>
                            <Chip
                              label={ESTADO_AGENDA_LABEL[tag]}
                              size="small"
                              sx={{ bgcolor: ESTADO_AGENDA_COLOR[tag], color: '#fff', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelect(v);
                              }}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Modal de detalle: Calidad de Visita (embebida) + Ticket ligado (solo lectura). */}
      <ModalDetalleVisitaAgenda open={openDetail} onClose={handleCloseDetail} agendamiento={selected} />
    </>
  );
};

export default AgendaPage;
